import {
  Box,
  Button,
  Checkbox,
  Flex,
  Text,
  Textarea,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CSVLink } from "react-csv";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { toast } from "react-toastify";
import Layout from "../../../components/dashboard/Layout";
import LogoutMenu from "../../../components/dashboard/Layout/logout-menu";
import CTable from "../../../components/Table/table";
import {
  getPaidUnpaidStudentsRequest,
  sendAttendanceCustomMessageRequest,
} from "../../../modules/admin-modules/reports/Actions";
import { getPaymentSubjectRequest } from "../../../modules/admin-modules/videos/Actions";

const StudentCheckbox = React.memo(({ studentId, isSelected, onToggle }) => (
  <Checkbox
    isChecked={isSelected}
    onChange={(e) => onToggle(studentId, e.target.checked)}
  />
));

const PaidUnpaidStudentsReport = () => {
  const dispatch = useDispatch();
  const [paidSubjects, setPaidSubjects] = useState([]);
  const [unpaidSubjects, setUnpaidSubjects] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [message, setMessage] = useState("");
  const [size, setPerPage] = useState(10);
  const [pageNumber, setPageNumber] = useState(0);

  const paymentSubjects = useSelector((state) => state.videos.paymentSubjects);
  const students = useSelector((state) => state.reports.paidUnpaidStudents);
  const isLoading = useSelector((state) => state.reports.isLoading);

  useEffect(() => {
    dispatch(getPaymentSubjectRequest());
  }, [dispatch]);

  useEffect(() => {
    setSelectedStudents([]);
  }, [students]);

  const paymentSubjectsOptions = paymentSubjects?.map((data) => ({
    value: data.id,
    label: data.name,
  }));

  const colourStyles = {
    control: (styles) => ({
      ...styles,
      width: "100%",
      minWidth: "200px",
      background: "#f5f5f5",
      color: "#690000",
      borderRadius: "16px",
    }),
    option: (styles) => ({ ...styles, color: "#690000" }),
  };

  const getStudentId = (data) => data?.id || data?.student_id || data?.user_id;

  const handleFetch = () => {
    if (!paidSubjects?.length && !unpaidSubjects?.length) return;
    dispatch(
      getPaidUnpaidStudentsRequest({
        paid: paidSubjects.map((item) => item.value),
        unpaid: unpaidSubjects.map((item) => item.value),
      })
    );
  };

  const handleStudentToggle = useCallback((studentId, isChecked) => {
    setSelectedStudents((prev) => {
      if (isChecked) return [...prev, studentId];
      return prev.filter((id) => id !== studentId);
    });
  }, []);

  const handleSelectAll = useCallback(
    (isChecked) => {
      if (isChecked) {
        setSelectedStudents(
          students?.map((student) => getStudentId(student)).filter(Boolean) || []
        );
      } else {
        setSelectedStudents([]);
      }
    },
    [students]
  );

  const handleSendMessage = () => {
    if (!message.trim()) {
      toast.error("أدخل الرسالة أولاً");
      return;
    }
    if (!selectedStudents.length) {
      toast.error("اختر طالب واحد على الأقل");
      return;
    }
    dispatch(
      sendAttendanceCustomMessageRequest({
        user_ids: selectedStudents,
        message: message.trim(),
      })
    );
  };

  const headers = [
    { label: "name", key: "name" },
    { label: "username", key: "username" },
    { label: "phone", key: "phone" },
    { label: "parent_phone", key: "parent_phone" },
    { label: "group_name", key: "group_name" },
  ];

  const Data = useMemo(
    () =>
      students?.map((data, index) => {
        const studentId = getStudentId(data);
        return {
          ...data,
          index: `#${index + 1}`,
          id: studentId,
          select: (
            <StudentCheckbox
              studentId={studentId}
              isSelected={selectedStudents.includes(studentId)}
              onToggle={handleStudentToggle}
            />
          ),
          name: data?.name,
          username: data?.username || data?.user_name,
          phone: data?.phone,
          parent_phone: data?.parent_phone,
          group_name: data?.group?.name || data?.group_name,
        };
      }),
    [students, selectedStudents, handleStudentToggle]
  );

  const columns = [
    { Header: "اختيار", accessor: "select" },
    { Header: "#", accessor: "index" },
    { Header: "اسم الطالب", accessor: "name" },
    { Header: "كود الطالب", accessor: "username" },
    { Header: "المجموعة", accessor: "group_name" },
    { Header: "رقم الطالب", accessor: "phone" },
    { Header: "رقم ولي الأمر", accessor: "parent_phone" },
  ];

  const allSelected =
    students?.length > 0 &&
    selectedStudents.length === students.length;

  return (
    <Layout>
      <div style={{ minHeight: "400px" }}>
        <Flex
          w="100%"
          my="30px"
          justifyContent="space-between"
          alignItems="center"
          position="relative"
          zIndex="100"
          flexWrap="wrap"
          gap={3}
        >
          <Text fontSize="24px" fontWeight="600" color="main_1">
            تقرير الدفع وعدم الدفع
          </Text>
          <Flex
            w={{ base: "100%", md: "75%" }}
            alignItems="center"
            gap={2}
            flexWrap="wrap"
            justifyContent="flex-end"
          >
            <Box flex="1" minW="200px">
              <Select
                options={paymentSubjectsOptions}
                isMulti={true}
                value={paidSubjects}
                onChange={(data) => setPaidSubjects(data || [])}
                styles={colourStyles}
                placeholder="المدفوعات المدفوعة"
              />
            </Box>
            <Box flex="1" minW="200px">
              <Select
                options={paymentSubjectsOptions}
                isMulti={true}
                value={unpaidSubjects}
                onChange={(data) => setUnpaidSubjects(data || [])}
                styles={colourStyles}
                placeholder="المدفوعات غير المدفوعة"
              />
            </Box>
            <Button
              colorScheme="red"
              borderRadius="16px"
              onClick={handleFetch}
              isDisabled={!paidSubjects?.length && !unpaidSubjects?.length}
              isLoading={isLoading}
            >
              عرض
            </Button>
            <Box border="1px solid gray" borderRadius="8px" padding="10px">
              <CSVLink
                headers={headers}
                data={Data?.length != 0 ? Data : []}
                filename={"paid-unpaid-students.csv"}
                className="btn btn-primary"
                target="_blank"
              >
                Download
              </CSVLink>
            </Box>
            <LogoutMenu />
          </Flex>
        </Flex>

        {students?.length > 0 && (
          <Box mb={4} p={4} bg="white" borderRadius="md" border="1px solid #e2e8f0">
            <Flex
              justifyContent="space-between"
              alignItems="center"
              mb={3}
              flexWrap="wrap"
              gap={2}
            >
              <Checkbox
                isChecked={allSelected}
                isIndeterminate={
                  selectedStudents.length > 0 &&
                  selectedStudents.length < (students?.length || 0)
                }
                onChange={(e) => handleSelectAll(e.target.checked)}
              >
                تحديد الكل ({selectedStudents.length}/{students.length})
              </Checkbox>
              <Button
                colorScheme="orange"
                onClick={handleSendMessage}
                isDisabled={!selectedStudents.length || !message.trim()}
                isLoading={isLoading}
              >
                إرسال الرسالة
              </Button>
            </Flex>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="اكتب الرسالة المخصصة..."
              rows={4}
              bg="#f5f5f5"
              borderRadius="12px"
            />
          </Box>
        )}

        <CTable
          selectedData={() => {}}
          Columns={columns}
          Data={Data}
          path={"openModel"}
          Actions
          Title={``}
          subTitle=""
          btnTitle=""
          placeHolder=""
          noSearchBar={true}
          noFilter={true}
          footerBtnTitle
          filterList
          addButton
          addButtonTitle=""
          setPage={() => {}}
          setPerPage={setPerPage}
          currentpage={pageNumber}
          setPageNumber={setPageNumber}
          noSecondSearchBar={true}
          isLoading={isLoading}
          perPage={size}
          totalPage={Math.ceil((Data?.length || 0) / size) || 1}
        />
      </div>
    </Layout>
  );
};

export default PaidUnpaidStudentsReport;
